import { format } from 'date-fns';

/**
 * How many times each popup has already been shown today.
 *
 * For a signed-in member the authoritative tally is svc-notify's — it rides
 * back on the popup list response, so a cache clear, a second device or an
 * incognito window no longer hands the member a fresh allowance. What is stored
 * here is that member's local mirror and, for an ANONYMOUS visitor, the tally
 * itself: there is no id to count that half by, and inventing one would be a
 * tracking cookie in all but name. `effectiveDayCounts` picks between them.
 *
 * `date` is a local calendar day. A stored day that is not today is discarded
 * whole, so the record never grows past one day of ids.
 */
export interface DayCounts {
  date: string;
  counts: Record<string, number>;
}

/** Local calendar day, the boundary the cap resets on. */
export function todayKey(now: Date): string {
  return format(now, 'yyyy-MM-dd');
}

export function emptyDayCounts(today: string): DayCounts {
  return { date: today, counts: {} };
}

/**
 * Parse a persisted record, discarding anything that is not today's or is not
 * the shape we wrote. Storage is shared with the user's own devtools and with
 * older builds, so nothing read back here is trusted.
 */
function sanitizeCounts(source: Record<string, unknown> | undefined): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const [id, value] of Object.entries(source ?? {})) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      counts[id] = Math.floor(value);
    }
  }
  return counts;
}

export function parseDayCounts(raw: string | null, today: string): DayCounts {
  if (!raw) return emptyDayCounts(today);
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyDayCounts(today);
  }
  if (!parsed || typeof parsed !== 'object') return emptyDayCounts(today);
  const record = parsed as Partial<DayCounts>;
  if (record.date !== today) return emptyDayCounts(today);
  return { date: today, counts: sanitizeCounts(record.counts) };
}

/**
 * Which tally the cap is measured against.
 *
 * A member's counts come from svc-notify; a visitor's come from this device. A
 * member whose list has not answered yet falls back to the device rather than
 * to nothing — an unknown tally must never read as an unspent allowance. The
 * server's counts are sanitised exactly like stored ones: this is still a value
 * off the wire, and one bad entry must not decide what a player sees.
 */
export function effectiveDayCounts(opts: {
  today: string;
  authed: boolean;
  local: DayCounts;
  server?: Record<string, number> | null | undefined;
}): DayCounts {
  if (!opts.authed || opts.server == null) return opts.local;
  return { date: opts.today, counts: sanitizeCounts(opts.server) };
}

export function recordShow(state: DayCounts, id: string): DayCounts {
  return { date: state.date, counts: { ...state.counts, [id]: (state.counts[id] ?? 0) + 1 } };
}

/**
 * True once the popup has had its allowance for today.
 *
 * A null, zero or negative cap means uncapped — a BO operator who clears the box
 * gets the pre-#231 behaviour, and a nonsense value never silently hides a
 * creative the operator meant to run.
 */
export function isCappedToday(
  state: DayCounts,
  id: string,
  maxShowsPerDay: number | null | undefined,
): boolean {
  if (maxShowsPerDay == null || maxShowsPerDay <= 0) return false;
  return (state.counts[id] ?? 0) >= maxShowsPerDay;
}
